import { useState, useRef, useEffect, useCallback } from "react";
import {
  ChevronLeft,
  ChevronRight,
  List,
  X,
  Volume2,
  Loader2,
  SkipForward,
  Lock,
  Presentation,
  Trash2,
  Clock,
  CheckCircle,
} from "lucide-react";
import { Button } from "@learn-me/ui";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AudioPlayer } from "./AudioPlayer";
import { DiagramRenderer } from "../DiagramRenderer";
import { SectionPresentationMode } from "./SectionPresentationMode";
import { diagramsApi } from "@/lib/diagramsApi";
import { marked } from "marked";

// Configure marked for better output
marked.setOptions({
  gfm: true,
  breaks: true,
});

export interface Section {
  id: string;
  title: string;
  content: string;
  audioUrl?: string;
  audioDuration?: number;
  position: number;
  /** Featured image for slideshow experience */
  featuredImageUrl?: string;
}

interface SectionGating {
  maxAccessiblePosition: number;
  requiresSequential: boolean;
}

const MINIMUM_DISPLAY_SECONDS = 60;

interface SectionProgress {
  sectionId: string;
  isViewed: boolean;
  timeSpent: number;
  requiredSeconds: number;
  remainingSeconds: number;
}

interface SectionPlayerProps {
  sections: Section[];
  chapterTitle?: string;
  chapterAudioUrl?: string;
  chapterAudioDuration?: number;
  initialSection?: number;
  autoPlayBetweenSections?: boolean;
  onSectionChange?: (sectionIndex: number) => void;
  onSectionViewed?: (sectionId: string) => boolean | Promise<boolean>;
  viewedSectionIds?: string[];
  sectionProgress?: SectionProgress[];
  gating?: SectionGating;
  /** Enable edit mode - content becomes clickable */
  editable?: boolean;
  /** Callback when content is clicked in edit mode */
  onContentClick?: (sectionId: string) => void;
  /** Callback when title is clicked in edit mode */
  onTitleClick?: (sectionId: string) => void;
  /** Custom content renderer for edit mode (replaces default ContentRenderer) */
  renderContent?: (section: Section) => React.ReactNode;
  /** Custom title renderer for edit mode */
  renderTitle?: (section: Section) => React.ReactNode;
  /** Custom audio controls for admin mode (rendered below audio player) */
  renderAudioControls?: (section: Section) => React.ReactNode;
}

/**
 * Renders chapter content with section-based navigation.
 * Supports microlearning by allowing users to navigate between sections.
 * Supports deep linking via initialSection prop.
 * Supports auto-play to automatically advance to the next section when audio ends.
 */
export function SectionPlayer({
  sections,
  chapterTitle,
  chapterAudioUrl,
  chapterAudioDuration,
  initialSection = 0,
  autoPlayBetweenSections = false,
  onSectionChange,
  onSectionViewed,
  viewedSectionIds,
  sectionProgress,
  gating,
  editable = false,
  onContentClick,
  onTitleClick,
  renderContent,
  renderTitle,
  renderAudioControls,
}: SectionPlayerProps) {
  const [currentSectionIndex, setCurrentSectionIndex] = useState(
    Math.min(Math.max(0, initialSection), sections.length - 1),
  );
  const [showNav, setShowNav] = useState(false);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(
    autoPlayBetweenSections,
  );
  const [showPresentationMode, setShowPresentationMode] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const viewedSectionsRef = useRef<Set<string>>(new Set());
  const pendingViewedSectionsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    viewedSectionsRef.current = new Set(viewedSectionIds ?? []);
  }, [viewedSectionIds]);

  const currentSection = sections[currentSectionIndex];
  const hasMultipleSections = sections.length > 1;
  const currentSectionProgress = currentSection
    ? sectionProgress?.find(
        (progress) => progress.sectionId === currentSection.id,
      )
    : undefined;
  const currentRequiredSeconds =
    currentSectionProgress?.requiredSeconds ?? MINIMUM_DISPLAY_SECONDS;
  const currentTimeSpent = currentSectionProgress?.timeSpent ?? 0;
  const currentRemainingSeconds =
    currentSectionProgress?.remainingSeconds ??
    (currentSectionProgress?.isViewed
      ? 0
      : Math.max(0, currentRequiredSeconds - currentTimeSpent));
  const currentUnlockPercent =
    currentRequiredSeconds > 0
      ? Math.min(
          100,
          Math.round((currentTimeSpent / currentRequiredSeconds) * 100),
        )
      : 100;

  // Check if a section is accessible based on gating rules
  const isSectionAccessible = useCallback(
    (index: number): boolean => {
      if (!gating || !gating.requiresSequential) {
        return true;
      }
      const section = sections[index];
      if (!section) return false;
      return section.position <= gating.maxAccessiblePosition;
    },
    [gating, sections],
  );

  const clampToAccessibleSection = useCallback(
    (index: number): number => {
      if (sections.length === 0) return 0;

      const boundedIndex = Math.min(Math.max(0, index), sections.length - 1);
      if (isSectionAccessible(boundedIndex)) return boundedIndex;

      if (!gating?.requiresSequential) return boundedIndex;

      const maxAccessibleIndex = Math.min(
        Math.max(0, gating.maxAccessiblePosition - 1),
        sections.length - 1,
      );
      return maxAccessibleIndex;
    },
    [gating, isSectionAccessible, sections.length],
  );

  // Sync internal state with external initialSection prop (for controlled navigation)
  useEffect(() => {
    const clampedIndex = clampToAccessibleSection(initialSection);
    if (clampedIndex !== currentSectionIndex) {
      setCurrentSectionIndex(clampedIndex);
      onSectionChange?.(clampedIndex);
    }
  }, [
    clampToAccessibleSection,
    currentSectionIndex,
    initialSection,
    onSectionChange,
  ]);

  const goToSection = useCallback(
    (index: number) => {
      if (index >= 0 && index < sections.length && isSectionAccessible(index)) {
        setCurrentSectionIndex(index);
        onSectionChange?.(index);
        setShowNav(false);
        // Scroll to top of content
        contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [sections.length, onSectionChange, isSectionAccessible],
  );

  const goToPrev = useCallback(() => {
    goToSection(currentSectionIndex - 1);
  }, [currentSectionIndex, goToSection]);

  const goToNext = useCallback(() => {
    goToSection(currentSectionIndex + 1);
  }, [currentSectionIndex, goToSection]);

  // Check if next section is accessible
  const isNextAccessible =
    currentSectionIndex < sections.length - 1 &&
    isSectionAccessible(currentSectionIndex + 1);

  // Auto-advance when audio ends (if enabled and next section is accessible)
  const handleAudioEnded = useCallback(() => {
    if (autoPlayEnabled && isNextAccessible) {
      goToNext();
    }
  }, [autoPlayEnabled, isNextAccessible, goToNext]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && currentSectionIndex > 0) {
        goToPrev();
      } else if (
        e.key === "ArrowRight" &&
        currentSectionIndex < sections.length - 1
      ) {
        goToNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentSectionIndex, sections.length, goToPrev, goToNext]);

  // Mark section as viewed when it becomes current
  useEffect(() => {
    const section = sections[currentSectionIndex];
    if (!section || !onSectionViewed) return;

    if (
      viewedSectionsRef.current.has(section.id) ||
      pendingViewedSectionsRef.current.has(section.id)
    ) {
      return;
    }

    pendingViewedSectionsRef.current.add(section.id);

    void Promise.resolve(onSectionViewed(section.id))
      .then((marked) => {
        if (marked) {
          viewedSectionsRef.current.add(section.id);
        }
      })
      .finally(() => {
        pendingViewedSectionsRef.current.delete(section.id);
      });
  }, [currentSectionIndex, sections, onSectionViewed]);

  if (!sections.length) {
    return null;
  }

  // If only one section, render simple content
  if (!hasMultipleSections) {
    return (
      <div className="section-player">
        {/* Chapter-level audio if available */}
        {chapterAudioUrl && (
          <div className="mb-6">
            <AudioPlayer
              audioUrl={chapterAudioUrl}
              audioDuration={chapterAudioDuration}
              title={chapterTitle}
            />
          </div>
        )}

        {renderContent ? (
          renderContent(currentSection!)
        ) : (
          <div
            onClick={
              editable && currentSection
                ? () => onContentClick?.(currentSection.id)
                : undefined
            }
            className={
              editable
                ? "cursor-pointer hover:bg-gray-50 rounded-lg transition-colors -mx-2 px-2 py-1"
                : ""
            }
          >
            <ContentRenderer html={currentSection?.content || ""} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="section-player">
      {/* Section Navigation Header - hidden in editable mode (WYSIWYG has its own navigation) */}
      {!editable && (
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b mb-6 -mx-6 px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Progress indicator */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowNav(!showNav)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Ver todas las secciones"
              >
                <List className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {currentSectionIndex + 1} / {sections.length}
                </span>
              </button>

              {/* Section title */}
              <span className="text-sm text-gray-600 truncate max-w-[200px] hidden sm:inline">
                {currentSection?.title}
              </span>
            </div>

            {/* Navigation arrows and auto-play toggle */}
            <div className="flex items-center gap-1">
              {/* Presentation mode button */}
              <button
                onClick={() => setShowPresentationMode(true)}
                className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-gray-500 hover:bg-gray-100 transition-colors"
                title="Modo presentación"
              >
                <Presentation className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Presentar</span>
              </button>

              {/* Auto-play toggle */}
              <button
                onClick={() => setAutoPlayEnabled(!autoPlayEnabled)}
                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors ${
                  autoPlayEnabled
                    ? "bg-primary-100 text-primary-700"
                    : "text-gray-500 hover:bg-gray-100"
                }`}
                title={
                  autoPlayEnabled
                    ? "Desactivar auto-avance"
                    : "Activar auto-avance"
                }
              >
                <SkipForward className="h-3 w-3" />
                <span className="hidden sm:inline">Auto</span>
              </button>

              <Button
                variant="ghost"
                size="sm"
                onClick={goToPrev}
                disabled={currentSectionIndex === 0}
                aria-label="Sección anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={goToNext}
                disabled={!isNextAccessible}
                aria-label="Siguiente sección"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 transition-all duration-300"
              style={{
                width: `${((currentSectionIndex + 1) / sections.length) * 100}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Section Navigation Panel (overlay) */}
      {showNav && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40"
            onClick={() => setShowNav(false)}
          />
          <div className="fixed inset-y-0 left-0 w-80 max-w-[85vw] bg-white z-50 shadow-xl overflow-hidden flex flex-col animate-slide-in-left">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold">Secciones</h3>
              <button
                onClick={() => setShowNav(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {sections.map((section, index) => {
                const isActive = index === currentSectionIndex;
                const hasAudio = !!section.audioUrl;
                const isAccessible = isSectionAccessible(index);
                const isViewed = viewedSectionsRef.current.has(section.id);

                return (
                  <button
                    key={section.id}
                    onClick={() => isAccessible && goToSection(index)}
                    disabled={!isAccessible}
                    className={`w-full text-left p-3 rounded-lg mb-1 flex items-center gap-3 transition-colors ${
                      isActive
                        ? "bg-primary-50 text-primary-700 border border-primary-200"
                        : !isAccessible
                          ? "opacity-60 cursor-not-allowed"
                          : "hover:bg-gray-50"
                    }`}
                  >
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-sm ${
                        isActive
                          ? "bg-primary-500 text-white"
                          : !isAccessible
                            ? "bg-gray-200 text-gray-400"
                            : isViewed
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {!isAccessible ? (
                        <Lock className="h-3.5 w-3.5" />
                      ) : (
                        index + 1
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {section.title}
                      </p>
                      {!isAccessible && gating?.requiresSequential && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          Completa la sección anterior
                        </p>
                      )}
                      {isAccessible && hasAudio && (
                        <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                          <Volume2 className="h-3 w-3" />
                          <span>Audio disponible</span>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Section Content */}
      <div ref={contentRef} className="section-content">
        {/* Section audio (if available) */}
        {currentSection?.audioUrl && (
          <div className="mb-6">
            <AudioPlayer
              audioUrl={currentSection.audioUrl}
              audioDuration={currentSection.audioDuration}
              title={currentSection.title}
              onEnded={handleAudioEnded}
            />
            {/* Admin audio controls */}
            {renderAudioControls && currentSection && (
              <div className="mt-2">{renderAudioControls(currentSection)}</div>
            )}
          </div>
        )}

        {/* Fallback to chapter audio if section doesn't have audio */}
        {!currentSection?.audioUrl && chapterAudioUrl && (
          <div className="mb-6">
            <AudioPlayer
              audioUrl={chapterAudioUrl}
              audioDuration={chapterAudioDuration}
              title={chapterTitle}
              onEnded={handleAudioEnded}
            />
          </div>
        )}

        {/* No audio - show admin controls to generate if in edit mode */}
        {!currentSection?.audioUrl &&
          !chapterAudioUrl &&
          renderAudioControls &&
          currentSection && (
            <div className="mb-6">{renderAudioControls(currentSection)}</div>
          )}

        {/* Audio loading indicator (when no audio available and not in edit mode) */}
        {!currentSection?.audioUrl &&
          !chapterAudioUrl &&
          !renderAudioControls && (
            <div className="mb-6 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Loader2 className="h-4 w-4 text-purple-600 animate-spin" />
                </div>
                <div>
                  <span className="text-sm font-medium text-purple-800">
                    Audio en generación
                  </span>
                  <p className="text-xs text-purple-600 mt-0.5">
                    El audio de esta sección se está generando. Estará
                    disponible pronto.
                  </p>
                </div>
              </div>
            </div>
          )}

        {/* Featured Image (Slideshow Header) */}
        {currentSection?.featuredImageUrl && (
          <div className="mb-6 rounded-xl overflow-hidden shadow-lg">
            <img
              src={currentSection.featuredImageUrl}
              alt={currentSection.title}
              className="w-full h-auto max-h-[400px] object-cover"
            />
          </div>
        )}

        {/* Section title */}
        {renderTitle && currentSection ? (
          renderTitle(currentSection)
        ) : (
          <h2
            onClick={
              editable && currentSection
                ? () => onTitleClick?.(currentSection.id)
                : undefined
            }
            className={`text-2xl font-bold text-gray-900 mb-4 ${
              editable
                ? "cursor-pointer hover:bg-gray-50 rounded-lg transition-colors -mx-2 px-2 py-1"
                : ""
            }`}
          >
            {currentSection?.title}
          </h2>
        )}

        {/* Section content */}
        {!editable && currentSectionProgress && (
          <div
            className={`mb-5 rounded-lg border px-4 py-3 ${
              currentSectionProgress.isViewed
                ? "border-green-200 bg-green-50 text-green-800"
                : "border-amber-200 bg-amber-50 text-amber-800"
            }`}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 text-sm font-medium">
                {currentSectionProgress.isViewed ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <Clock className="h-4 w-4" />
                )}
                <span>
                  {currentSectionProgress.isViewed
                    ? "Sección completada. Ya puedes continuar."
                    : `Permanece ${currentRemainingSeconds}s más para desbloquear la siguiente sección.`}
                </span>
              </div>
              <span className="text-xs">
                {Math.min(currentTimeSpent, currentRequiredSeconds)}s /{" "}
                {currentRequiredSeconds}s
              </span>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/80">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  currentSectionProgress.isViewed
                    ? "bg-green-500"
                    : "bg-amber-500"
                }`}
                style={{ width: `${currentUnlockPercent}%` }}
              />
            </div>
          </div>
        )}

        {renderContent ? (
          renderContent(currentSection!)
        ) : (
          <div
            onClick={
              editable && currentSection
                ? () => onContentClick?.(currentSection.id)
                : undefined
            }
            className={
              editable
                ? "cursor-pointer hover:bg-gray-50 rounded-lg transition-colors -mx-2 px-2 py-1"
                : ""
            }
          >
            <ContentRenderer html={currentSection?.content || ""} />
          </div>
        )}

        {/* Section diagrams */}
        {currentSection && (
          <SectionDiagrams sectionId={currentSection.id} editable={editable} />
        )}

        {/* Bottom navigation - hidden in editable mode */}
        {!editable && (
          <div className="flex justify-between items-center mt-8 pt-6 border-t">
            <div>
              {currentSectionIndex > 0 && (
                <Button variant="outline" onClick={goToPrev}>
                  <ChevronLeft className="mr-2 h-4 w-4" />
                  {sections[currentSectionIndex - 1]?.title}
                </Button>
              )}
            </div>
            <div>
              {currentSectionIndex < sections.length - 1 &&
                (isNextAccessible ? (
                  <Button onClick={goToNext}>
                    {sections[currentSectionIndex + 1]?.title}
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 px-4 py-2 rounded-lg">
                    <Lock className="h-4 w-4" />
                    <span>
                      {currentRemainingSeconds > 0
                        ? `Siguiente disponible en ${currentRemainingSeconds}s`
                        : "Completa esta sección para continuar"}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Presentation Mode */}
      {showPresentationMode && (
        <SectionPresentationMode
          sections={sections}
          currentIndex={currentSectionIndex}
          onIndexChange={(index) => {
            if (!isSectionAccessible(index)) return;
            setCurrentSectionIndex(index);
            onSectionChange?.(index);
          }}
          onClose={() => setShowPresentationMode(false)}
          canAccessIndex={isSectionAccessible}
          chapterTitle={chapterTitle}
        />
      )}
    </div>
  );
}

/**
 * Renders HTML content with prose styling
 * Exported for reuse in WYSIWYG editor
 */
export function ContentRenderer({ html }: { html: string }) {
  // Detect if content is Markdown
  const isMarkdown = !html.trim().startsWith("<");

  const processedHtml = isMarkdown ? (marked.parse(html) as string) : html;

  return (
    <div
      className="prose prose-sm sm:prose max-w-none prose-headings:font-semibold prose-a:text-primary-600 prose-code:text-primary-700 prose-code:bg-primary-50 prose-code:px-1 prose-code:rounded"
      dangerouslySetInnerHTML={{ __html: processedHtml }}
    />
  );
}

/**
 * Fetches and renders diagrams for a section
 */
function SectionDiagrams({
  sectionId,
  editable = false,
}: {
  sectionId: string;
  editable?: boolean;
}) {
  const queryClient = useQueryClient();
  const { data: diagrams = [], isLoading } = useQuery({
    queryKey: ["section-diagrams", sectionId],
    queryFn: () => diagramsApi.getSectionDiagrams(sectionId),
    staleTime: 60000, // Cache for 1 minute
  });

  const deleteMutation = useMutation({
    mutationFn: (diagramId: string) => diagramsApi.deleteDiagram(diagramId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["section-diagrams", sectionId],
      });
      toast.success("Diagrama eliminado");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Error al eliminar diagrama",
      );
    },
  });

  // Filter to only show completed diagrams
  const completedDiagrams = diagrams.filter(
    (d) => d.status === "COMPLETED" && d.mermaidCode,
  );

  if (isLoading || completedDiagrams.length === 0) {
    return null;
  }

  return (
    <div className="mt-8 space-y-6">
      <h3 className="text-lg font-semibold text-gray-800">Diagramas</h3>
      {completedDiagrams.map((diagram) => (
        <div key={diagram.id} className="relative group">
          <DiagramRenderer
            code={diagram.mermaidCode}
            title={diagram.title}
            className="shadow-sm"
          />
          {editable && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (window.confirm("¿Eliminar este diagrama?")) {
                  deleteMutation.mutate(diagram.id);
                }
              }}
              disabled={deleteMutation.isPending}
              className="absolute top-2 right-2 text-red-600 hover:text-red-700 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Eliminar diagrama"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}
